import { ProfileOutlined } from "@ant-design/icons";
import { assertNever } from "@p0security/shared/types/index";
import { IdpGroup, IdpGroups } from "@p0security/shared/types/workflow/types";
import { Flex, Typography } from "antd";
import { PermissionIntegrationLogo } from "components/Integrations/IntegrationLogo";
import {
  RoutingRulePopover,
  StyledRoutingRulePreviewComponent,
} from "components/Jit/Routing/styles";
import { useCallback, useContext } from "react";

import { RoutingRuleEditorContext } from "../../store/RoutingRuleEditorContext";
import { IdpGroupInput } from "../IdpGroupInput";

type Props = {
  idpGroups: IdpGroup[];
  isApproval?: boolean;
  showPopover?: boolean;
  effect?: "keep" | "remove";
};

export const IdpGroupPreview: React.FC<{
  idpGroup: IdpGroup;
  showPopover?: boolean;
}> = ({ idpGroup, showPopover = true }) => {
  return (
    <RoutingRulePopover
      text={idpGroup.label}
      icon={PermissionIntegrationLogo[idpGroup.directory]}
      popoverContent={showPopover ? idpGroup.id : undefined}
    />
  );
};

export const IdpGroupsPreview: React.FC<Props> = ({
  idpGroups,
  effect = "keep",
  isApproval = false,
  showPopover = true,
}) => {
  const getBaseText = (effect: "keep" | "remove", length: number) => {
    switch (effect) {
      case "keep":
        return length === 1 ? "in group" : "in any of groups";
      case "remove":
        return "not in";
      default:
        throw assertNever(effect);
    }
  };

  return (
    <Flex vertical gap={4}>
      {isApproval ? (
        <Typography.Text className="label">
          Approval by users{" " + getBaseText(effect, idpGroups.length)}
        </Typography.Text>
      ) : (
        <Typography.Text className="label">
          Identities{" " + getBaseText(effect, idpGroups.length)}
        </Typography.Text>
      )}

      <div className="pl-2">
        {idpGroups.map((group) => (
          <IdpGroupPreview
            key={group.id}
            idpGroup={group}
            showPopover={showPopover}
          />
        ))}
      </div>
    </Flex>
  );
};

export const RequestorIdpGroupPreview: React.FC<{ isApproval?: boolean }> = ({
  isApproval,
}) => {
  const { requestor, setRequestor, requestorError, setRequestorError } =
    useContext(RoutingRuleEditorContext);

  const handleIdpGroupChange = useCallback(
    (idpGroups: IdpGroups) => {
      setRequestor(idpGroups);
    },
    [setRequestor]
  );

  return (
    <StyledRoutingRulePreviewComponent
      icon={ProfileOutlined}
      label={
        isApproval
          ? "Approval by member of a directory group"
          : "Member of a directory group"
      }
      content={
        requestor?.type === "group" ? (
          <IdpGroupInput
            value={requestor}
            onChange={handleIdpGroupChange}
            error={requestorError}
            setError={setRequestorError}
            formItemProps={{
              colon: false,
              layout: "horizontal",
              style: { maxWidth: 500 },
            }}
          />
        ) : undefined
      }
    />
  );
};

export const ApprovalIdpGroupPreview: React.FC = () => (
  <StyledRoutingRulePreviewComponent
    icon={ProfileOutlined}
    label="Approval by member of a directory group"
  />
);
